import React, { useEffect, useState } from "react";
import api, { apiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, GlassCard } from "@/components/ui-kit";
import { Settings } from "lucide-react";
import { toast } from "sonner";
import { playSuccess } from "@/lib/sound";

const F = ({ label, children }) => (<div><label className="text-xs text-stone-500 mb-1 block">{label}</label>{children}</div>);
const inputCls = "w-full px-3 py-2 rounded-lg bg-amber-50 border border-amber-400/30 text-stone-800 text-sm focus:outline-none focus:border-amber-400";

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [school, setSchool] = useState(null);

  useEffect(() => {
    if (!isAdmin && user?.school_id) api.get(`/schools/${user.school_id}`).then((r) => setSchool(r.data)).catch(() => {});
  }, [user, isAdmin]);

  const set = (k, v) => setSchool((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!school.principal_name || !school.principal_name.trim()) return toast.error("اسم مدير المدرسة إجباري");
    try {
      await api.put(`/schools/${school.id}`, { name: school.name, stage: school.stage, type: school.type, address: school.address, phone: school.phone, coordinator_name: school.coordinator_name, principal_name: school.principal_name, students_count: Number(school.students_count) || 0 });
      playSuccess(); toast.success("تم حفظ بيانات المدرسة — بياناتك الدقيقة أساس نجاح مدرستك 🌟");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (isAdmin) {
    return (
      <div>
        <PageTitle title="الإعدادات" subtitle="إعدادات النظام" icon={Settings} />
        <GlassCard>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between border-b border-amber-400/10 py-2"><span className="text-stone-500">اسم النظام</span><span className="text-stone-800">ملف الإنجاز التطوعي — ملف إنجاز الأعمال التطوعية في مدارس منطقة حائل</span></div>
            <div className="flex justify-between border-b border-amber-400/10 py-2"><span className="text-stone-500">الحساب</span><span className="text-stone-800">{user?.email}</span></div>
            <div className="flex justify-between border-b border-amber-400/10 py-2"><span className="text-stone-500">سعر الساعة التطوعية</span><span className="text-amber-600">45.55 ريال</span></div>
            <div className="flex justify-between py-2"><span className="text-stone-500">المرجعية</span><span className="text-stone-800">المسؤولية المجتمعية والعمل التطوعي — الإدارة العامة للتعليم بمنطقة حائل</span></div>
          </div>
        </GlassCard>
      </div>
    );
  }

  if (!school) return <div className="text-stone-500">جارٍ التحميل...</div>;

  return (
    <div>
      <PageTitle title="إعدادات المدرسة" subtitle="تعديل بيانات مدرستك" icon={Settings} />
      <GlassCard className="max-w-2xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <F label="اسم المدرسة"><input data-testid="settings-name" value={school.name || ""} onChange={(e) => set("name", e.target.value)} className={inputCls} /></F>
          <F label="المرحلة"><input value={school.stage || ""} onChange={(e) => set("stage", e.target.value)} className={inputCls} /></F>
          <F label="النوع"><select value={school.type || "بنين"} onChange={(e) => set("type", e.target.value)} className={inputCls}><option value="بنين">بنين</option><option value="بنات">بنات</option></select></F>
          <F label="اسم المنسق"><input value={school.coordinator_name || ""} onChange={(e) => set("coordinator_name", e.target.value)} className={inputCls} /></F>
          <F label="اسم مدير المدرسة *"><input data-testid="settings-principal" value={school.principal_name || ""} onChange={(e) => set("principal_name", e.target.value)} className={inputCls} placeholder="إجباري — يظهر في التقارير الرسمية" /></F>
          <F label="العنوان"><input value={school.address || ""} onChange={(e) => set("address", e.target.value)} className={inputCls} /></F>
          <F label="الهاتف"><input value={school.phone || ""} onChange={(e) => set("phone", e.target.value)} className={inputCls} /></F>
          <F label="إجمالي عدد طلاب المدرسة (يُدخله مدير القسم)"><input data-testid="settings-students" type="number" value={school.students_count ?? 0} disabled readOnly className={`${inputCls} opacity-70 cursor-not-allowed`} title="يُحدّثه مدير القسم فقط — تزوّده بالعدد شفهياً" /></F>
          <F label="إجمالي عدد منسوبي المدرسة (يُدخله مدير القسم)"><input data-testid="settings-staff" type="number" value={school.staff_count ?? 0} disabled readOnly className={`${inputCls} opacity-70 cursor-not-allowed`} title="يُحدّثه مدير القسم فقط — تزوّده بالعدد شفهياً" /></F>
        </div>
        <button data-testid="save-settings-btn" onClick={save} className="btn-gold px-6 py-2.5 rounded-xl mt-5">حفظ التغييرات</button>
      </GlassCard>
    </div>
  );
}
